import { AudiencePlatformItem, CountryItem } from '@digiseg-labs/api-client';
import { EnvelopeClosedIcon, Link1Icon, Share1Icon } from '@radix-ui/react-icons';
import { Button, Checkbox, DropdownMenu, Flex, Text } from '@radix-ui/themes';
import { useEffect, useRef, useState } from 'react';

type ShareTarget =
  | { type: 'category'; code: string; name: string }
  | { type: 'audience'; code: string; name: string; categoryCode?: string }
  | { type: 'search'; code: 'search'; name: string; searchQuery: string };

export type BuildShareUrlOptions = {
  categoryCode?: string;
  audienceCode?: string;
  includeCountry?: boolean;
  includePlatform?: boolean;
  searchQuery?: string;
};

type ShareMenuProps = {
  target: ShareTarget;
  selectedCountry?: CountryItem;
  selectedPlatform?: AudiencePlatformItem;
  buildShareUrl: (options: BuildShareUrlOptions) => string;
};

const ShareMenu = ({ target, selectedCountry, selectedPlatform, buildShareUrl }: ShareMenuProps) => {
  const [includeCountry, setIncludeCountry] = useState(!!selectedCountry);
  const [includePlatform, setIncludePlatform] = useState(!!selectedPlatform);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasCountry = !!selectedCountry;
  const hasPlatform = !!selectedPlatform;

  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setIncludeCountry(!!selectedCountry);
  }, [selectedCountry]);

  useEffect(() => {
    setIncludePlatform(!!selectedPlatform);
  }, [selectedPlatform]);

  const setCopyMessage = (message: string) => {
    setCopyFeedback(message);
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }
    feedbackTimeoutRef.current = setTimeout(() => {
      setCopyFeedback(null);
      feedbackTimeoutRef.current = null;
    }, 2000);
  };

  const createShareUrl = () =>
    buildShareUrl({
      categoryCode: target.type === 'category' ? target.code : target.type === 'audience' ? target.categoryCode : undefined,
      audienceCode: target.type === 'audience' ? target.code : undefined,
      searchQuery:
        target.type === 'search'
          ? target.searchQuery
          : target.type === 'audience' || target.type === 'category'
            ? target.name
            : undefined,
      includeCountry: includeCountry && hasCountry,
      includePlatform: includePlatform && hasPlatform,
    });

  const handleCopyLink = async () => {
    const url = createShareUrl();
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopyMessage('Link copied');
    } catch (error) {
      console.error('Failed to copy link', error);
      setCopyMessage('Unable to copy');
    }
  };

  const handleEmailLink = () => {
    if (typeof window === 'undefined') return;
    const url = createShareUrl();
    const subject = encodeURIComponent(
      target.type === 'category'
        ? `Digiseg audience category: ${target.name}`
        : `Digiseg audience: ${target.name}`,
    );
    const body = encodeURIComponent(`Have a look at this ${target.type} on Digiseg:\n${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <Flex align="center" gap="2" justify="end">
      {copyFeedback && (
        <Text size="1" color="gray">
          {copyFeedback}
        </Text>
      )}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger style={{ paddingRight: '10px'}}>
          <div>
          <Button variant="ghost" color="gray" size="1">
            <Share1Icon />
            Share
          </Button>
          </div>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end">
          {(hasCountry || hasPlatform) && (
            <DropdownMenu.Group>
              {hasCountry && (
                <DropdownMenu.Item
                  onSelect={(event) => {
                    event.preventDefault();
                    setIncludeCountry((prev) => !prev);
                  }}
                >
                  <Flex align="center" gap="2">
                    <Checkbox checked={includeCountry} />
                    <Text size="2">Include {selectedCountry?.display_name}</Text>
                  </Flex>
                </DropdownMenu.Item>
              )}
              {hasPlatform && (
                <DropdownMenu.Item
                  onSelect={(event) => {
                    event.preventDefault();
                    setIncludePlatform((prev) => !prev);
                  }}
                >
                  <Flex align="center" gap="2">
                    <Checkbox checked={includePlatform} />
                    <Text size="2">Include {selectedPlatform?.display_name}</Text>
                  </Flex>
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Separator />
            </DropdownMenu.Group>
          )}
          <DropdownMenu.Item onSelect={handleCopyLink}>
            <Flex align="center" gap="2">
              <Link1Icon />
              Copy link
            </Flex>
          </DropdownMenu.Item>
          <DropdownMenu.Item onSelect={handleEmailLink}>
            <Flex align="center" gap="2">
              <EnvelopeClosedIcon />
              Send via email
            </Flex>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </Flex>
  );
};

export default ShareMenu;
